import { useEffect, useState } from "react";
import Service from "../service";
import { clearSharedSso } from "../utils/ssoCookie";

const readAutoLogoutSetting = () => {
  try {
    const userData = JSON.parse(localStorage.getItem("user_data") || "null");
    return !!userData?.companyId?.autoLogoutOnTabClose;
  } catch {
    return false;
  }
};

/**
 * When the company has `autoLogoutOnTabClose` enabled, fires a logout call as
 * the tab/browser is actually closing so the session slot frees immediately
 * instead of waiting for the idle-expiry window.
 *
 * The flag is read from the `user_data` snapshot cached in localStorage at
 * login, then kept in sync while this hook stays mounted via the
 * `localStorageChange` custom event that SettingsModule.js's
 * handleToggleAutoLogout dispatches after a successful toggle - the native
 * `storage` event only fires in OTHER tabs, never the tab that made the
 * change, so without this the toggle would only take effect on next login.
 *
 * Known, accepted limitations (not bugs):
 * - Browsers only expose `pagehide`/`beforeunload` here, and neither can tell a
 *   real tab close apart from a page refresh or a navigation to another site -
 *   a refresh destroys and recreates the whole JS context, so there is no way
 *   to "wait and see" across it. This will also log the user out on
 *   refresh/navigate-away.
 * - This hook is not multi-tab-aware: if the same account is open in two tabs,
 *   closing one tab logs out both, since they share the same access token/session.
 * - Only reacts within the browser tab where the setting was actually toggled.
 *   A colleague who is already logged in elsewhere won't see it take effect
 *   until they refresh/re-login there.
 */
const useAutoLogoutOnTabClose = () => {
  const [enabled, setEnabled] = useState(readAutoLogoutSetting);

  useEffect(() => {
    const handleCustomStorageChange = (e) => {
      if (e.detail?.key === "autoLogoutOnTabClose") {
        setEnabled(!!e.detail.newValue);
      }
    };
    window.addEventListener("localStorageChange", handleCustomStorageChange);
    return () => {
      window.removeEventListener("localStorageChange", handleCustomStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      return () => {};
    }

    const handlePageHide = () => {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) return;

      // Best-effort: end the server-side session immediately rather than
      // waiting for it to idle out. verifyUserToken checks the session
      // record on every request, so once this lands the old token is dead
      // everywhere, not just in this browser. The localStorage clear below
      // is still what makes this browser show a fresh login next time -
      // the fetch is keepalive and may outlive the page.
      fetch(`${Service.API_URL}${Service.logout}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        keepalive: true,
      }).catch(() => {});

      // Same cleanup as the manual "Logout" button (userSignOut in
      // appRedux/actions/Auth.js) - localStorage.removeItem is synchronous,
      // so this completes before the page is torn down even though the
      // fetch above may not.
      localStorage.removeItem("user_data");
      localStorage.removeItem("is_reporting_manager");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("resourceAccess");
      localStorage.removeItem("is_assistant_reporting_manager");
      localStorage.removeItem("ssoToken");
      localStorage.removeItem("companyDomain");
      clearSharedSso();
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [enabled]);
};

export default useAutoLogoutOnTabClose;
